import classNames from "classnames";
import ReportingConstants from "fleet/constant/reporting";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import PropTypes from "prop-types";
import React from "react";
import { Row, Col, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import { getStatusClass } from "redux-store/device-statuses/selectors";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { Table, Column } from "ui/table";
import { getErrorMessage } from "util/response";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

class AdDeliveryReportPage extends React.PureComponent {
  static get propTypes() {
    return {
      adsApp: PropTypes.object,
      devices: PropTypes.object.isRequired,
      fetchAdDeliveryReport: PropTypes.func.isRequired,
      networkOptions: PropTypes.array.isRequired,
      report: PropTypes.object.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      unloadAdDeliveryReport: PropTypes.func.isRequired,
    };
  }

  constructor() {
    super();

    this.state = {
      selected: null,
      submitted: false,
      errorMessage: "",
    };

    this.eventHeader = [
      ["device_status", "device_id", "name", "network", "recent_events"],
    ];
    this.tableOpts = {
      sortName: "deviceId",
      sortOrder: "desc",
    };

    this._onNetworkSelect = this._onNetworkSelect.bind(this);
    this._runReport = this._runReport.bind(this);
  }

  componentWillUnmount() {
    const { unloadAdDeliveryReport } = this.props;
    unloadAdDeliveryReport();
  }

  _onNetworkSelect(value) {
    this.setState({ selected: value });
  }

  _doSubmit() {
    const { value: networkId } = this.state.selected;
    const serviceArgs = { networkId };
    const { fetchAdDeliveryReport } = this.props;

    return fetchAdDeliveryReport(serviceArgs)
      .then(() => {
        this.setState({ errorMessage: "" });
      })
      .catch((e) => {
        this.setState({ errorMessage: getErrorMessage(e) });
      })
      .finally(() => {
        this.setState({ submitted: false });
      });
  }

  _runReport() {
    this.setState({ submitted: true }, this._doSubmit);
  }

  _getFormattedEvents(events) {
    if (isEmpty(events)) {
      return ["No recent events"];
    }

    return map(events, (v, k) => {
      const t = k.split(",");
      let name = ReportingConstants[t[0]][t[1]];
      if (t[0] === "INAPP" && t[1] === "CRASH") {
        name = t[2] === "False" ? name[1] : name[0];
      }

      return `${name} - ${v} times`;
    });
  }

  _formatStatus(status) {
    const deviceStatus = getStatusClass(status);
    return (
      <span className="device-status">
        <i className={deviceStatus.className} title={deviceStatus.label} />
      </span>
    );
  }

  _formatDeviceId(row, device) {
    return (
      <Link to={`/devices/${device.deviceId}/overview`}>{device.deviceId}</Link>
    );
  }

  _formatNetworkName(row, network) {
    return (
      <Link to={`/networks/${network.networkId}`}>{network.networkName}</Link>
    );
  }

  _formatEvents(events, row) {
    if (isEmpty(events)) {
      return <div>{this._getFormattedEvents(events)[0]}</div>;
    }

    return (
      <ul>
        {map(this._getFormattedEvents(events), (v, k) => {
          return <li key={k}>{v}</li>;
        })}
      </ul>
    );
  }

  _exportEventData(data) {
    const eventData = data.map((row) => {
      return [
        row.deviceStatus,
        row.deviceId,
        row.name,
        row.networkName,
        this._getFormattedEvents(row.events).join("\n"),
      ];
    });

    return [...this.eventHeader, ...eventData];
  }

  _renderReport() {
    const { devices, report } = this.props;
    const { devices: deviceIds, events } = report;
    if (!report || !deviceIds || deviceIds.length === 0) {
      return <p>No data to display.</p>;
    }

    const data = deviceIds
      .filter((deviceId) => devices[deviceId])
      .map((deviceId) => {
        const device = devices[deviceId];
        return {
          deviceStatus: device.status,
          deviceId: device.device_id,
          networkId: device.network_id,
          networkName: device.network_name,
          name: device.name,
          events: events[device.id],
        };
      });

    const exportData = this._exportEventData.bind(this, data);

    return (
      <Table
        data={data}
        hover={true}
        pagination={true}
        search={true}
        options={this.tableOpts}
        exportFile="ad-delivery.csv"
        exportFunc={exportData}
      >
        <Column
          key="deviceStatus"
          dataField="deviceStatus"
          dataSort={true}
          width="80px"
          dataAlign="center"
          dataFormat={this._formatStatus}
        >
          Status
        </Column>
        <Column
          key="deviceId"
          dataField="deviceId"
          dataSort={true}
          isKey={true}
          dataFormat={this._formatDeviceId}
        >
          Device Id
        </Column>
        <Column key="name" dataField="name" dataSort={true}>
          Name
        </Column>
        <Column
          key="networkName"
          dataField="networkName"
          dataSort={true}
          dataFormat={this._formatNetworkName}
        >
          Network
        </Column>
        <Column
          key="events"
          dataField="events"
          dataSort={false}
          dataFormat={this._formatEvents.bind(this)}
        >
          Recent Events
        </Column>
      </Table>
    );
  }

  _renderBoxes() {
    const { adsApp, networkOptions } = this.props;
    const { errorMessage, selected, submitted } = this.state;
    const btnClassNames = classNames("fa fa-refresh", { "fa-spin": submitted });

    if (!adsApp) {
      return (
        <div>
          This report is only available when you use Vistar Media Ads App
        </div>
      );
    }

    return (
      <div>
        <Box title="Filter">
          <Row>
            <Col xs={12}>
              <Notification error={errorMessage} />
            </Col>
          </Row>
          <Row>
            <Col xs={11}>
              <Select
                name="network-select"
                onChange={this._onNetworkSelect}
                options={networkOptions}
                placeholder="Select network"
                value={selected || null}
              />
            </Col>
            <Col xs={1}>
              <Button
                variant="primary"
                onClick={this._runReport}
                disabled={submitted}
              >
                <i className={btnClassNames} /> Run
              </Button>
            </Col>
          </Row>
        </Box>
        <Box title="Report">{this._renderReport()}</Box>
      </div>
    );
  }

  _renderView() {
    const { storesLoaded } = this.props;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    return this._renderBoxes();
  }

  render() {
    const desc = `This report returns the list of devices that haven't
      displayed an ad in the past hour`;
    const view = this._renderView();

    return (
      <Page title="Ad Delivery Report" desc={desc}>
        {view}
      </Page>
    );
  }
}

export { AdDeliveryReportPage };
